import {arrayOf, bool, func, number, shape, string} from 'prop-types';
import Card from '../../shared/Card';
import Header from '../../shared/Header';
import Input from '../../shared/Input';
import {Box, Stack} from '../../shared/layouts';
import Select from '../../shared/Select';
import {coverageObj} from './OrvPage';
import RadioButton from '../../shared/RadioButton';
import {getPeriodOptions} from '../../../utils/constants';
import FormStack from '../../shared/layouts/FormStack';
import i18n from "../../../i18n";
import {useTranslation} from "react-i18next";
import useIconLibrary from "../../../hooks/useIconLibrary";
import OverviewDetailRow from "../../shared/OverviewDetailRow";
import AdvisorDetails from '../../shared/AdvisorDetails';

const getDurationInMonthsOptions = () => new Array(361).fill().map((num, index) => {
  const years = Math.floor(index / 12);
  const months = index % 12;

  const yearText = i18n.t('{{years}} jaar', {years});
  const monthText = months === 1 ? i18n.t('{{months}} maand', {months}) : i18n.t('{{months}} maanden', {months});

  return ({
    value: index,
    label: `${years > 0 ? yearText : ''}${years > 0 && months > 0 ? ` ${i18n.t('en')} ` : ''}${months > 0 ? monthText : ''}`
  });
}).filter((item) => item.value !== 0);

const getPawnedOptions = () => [
  { value: false, label: i18n.t('Nee') },
  { value: true, label: i18n.t('Ja') },
];

const GeneralOrvSection = ({
  indexInsurance,
  insurance_company,
  insuranceCompanies,
  policy_number,
  setInsurance,
  premium,
  errors,
  pawned,
  canEditOrv,
  policy_holders,
  advisor = {},
}) => {
  const { t } = useTranslation();
  const { amount, period, duration_in_months } = premium;
  const readOnly = !canEditOrv;

  const pawnedOptions = getPawnedOptions();


  const setInsurancePremium = (e) => {
    const { name, value } = e.target;
    setInsurance({
      target: {
        name: 'premium',
        value: { ...premium, [name]: value },
      },
    });
  };

  const FormFiller = (props) => !readOnly && <Box {...props} />;
  const { HouseIcon } = useIconLibrary();

  return (
    <Card>
      <Stack space="m" width="100%">
        <Header
          title={t("Algemene gegevens")}
          icon={<HouseIcon height="2rem" width="2rem" />}
          variant="section"
        />
        <FormStack space="m" readOnly={readOnly}>
          {/* Row */}
          <Select
            label={t("Maatschappij")}
            autoFocus
            name="insurance_company"
            onChange={setInsurance}
            options={insuranceCompanies}
            value={insurance_company}
            errors={errors[`${indexInsurance}.insurance_company`]}
            readOnly={readOnly}
          />
          <FormFiller />
          {/* Row */}
          <Input
            label={t("Polisnummer")}
            name="policy_number"
            value={policy_number}
            onChange={setInsurance}
            errors={errors[`${indexInsurance}.policy_number`]}
            placeholder={t("Vul je polisnummer in")}
            readOnly={readOnly}
          />
          <FormFiller />
          <Input
            label={t("Premie")}
            isCurrency
            name="amount"
            value={amount}
            onChange={setInsurancePremium}
            errors={errors[`${indexInsurance}.premium.amount`]}
            readOnly={readOnly}
          />
          <Select
            label={t('Betaalfrequentie')}
            name="period"
            value={period}
            options={getPeriodOptions()}
            onChange={setInsurancePremium}
            errors={errors[`${indexInsurance}.premium.period`]}
            readOnly={readOnly}
          />
          {/* Row */}
          <Select
            label={t("Looptijd")}
            name="duration_in_months"
            value={duration_in_months}
            options={getDurationInMonthsOptions()}
            onChange={setInsurancePremium}
            errors={errors[`${indexInsurance}.premium.duration_in_months`]}
            isSearchable
            readOnly={readOnly}
          />
          <FormFiller />
          {/* Row */}
          <RadioButton
            label={t("Verpand")}
            name={`pawned_${indexInsurance}`}
            direction={'row'}
            value={pawned}
            options={pawnedOptions}
            onChange={(value) =>
              setInsurance({ target: { name: 'pawned', value } })
            }
            readOnly={readOnly}
          />
          <FormFiller />
          {policy_holders?.length > 0 && (
            <div>
              <b>{t('Verzekeringnemers')}</b>
              <Stack space={'none'} divider={'default'}>
                {policy_holders.map(name => (
                  <OverviewDetailRow
                    key={name}
                    descriptor={t('Verzekeringnemer')}
                    value={name}
                    hideIfEmpty
                  />
                ))}
              </Stack>
            </div>
          )}
          {Object.keys(advisor).length > 0 && <AdvisorDetails {...advisor} />}
        </FormStack>
      </Stack>
    </Card>
  );
};

GeneralOrvSection.propTypes = {
  setInsurance: func.isRequired,
  indexInsurance: number.isRequired,
  insurance_company: number,
  policy_number: string,
  coverages: arrayOf(shape(coverageObj)),
  premium: shape({
    amount: number,
    period: string,
    duration_in_months: number,
  }),
  insuranceCompanies: arrayOf(shape({ value: number, label: string })),
  errors: shape({}),
  canEditOrv: bool,
  policy_holders: arrayOf(string),
};

GeneralOrvSection.defaultProps = {
  insurance_company: null,
  policy_number: '',
  premium: { amount: null, period: '', duration_in_months: null },
  insuranceCompanies: [],
  coverages: [coverageObj],
  errors: {},
  canEditOrv: false,
  policy_holders: [],
};

export default GeneralOrvSection;
